import { Table as TableAnt } from "antd"
import { TableObjectsProvider } from "components/providers/Table.provider"
import type { ColumnsType } from "antd/es/table"
import type { ExpandableConfig } from "antd/es/table/interface"
import { MouseEvent } from "react"

type Props<ColumnsDataType> = {
	columns: ColumnsType<ColumnsDataType>
	data: ColumnsDataType[]
	className?: string
	scrollY?: number
	expandable?: ExpandableConfig<ColumnsDataType>
	isLoading?: boolean
	onRow?: (record: ColumnsDataType) => {
		onMouseEnter?: (event: MouseEvent<HTMLTableRowElement>) => void
		onMouseLeave?: (event: MouseEvent<HTMLTableRowElement>) => void
		onClick?: (event: MouseEvent<HTMLTableRowElement>) => void
	}
	bordered?: boolean
	showHeader?: boolean
	/**
	 * Render only the visible rows. Requires a numeric `scrollX` and a width on
	 * every column. NOTE: antd's virtual body is div-based — it emits no
	 * `<tr>`/`<td>` — so anything selecting table elements must be checked first.
	 */
	virtual?: boolean
	scrollX?: number
	/**
	 * antd only switches to `table-layout: fixed` on its own when a column sets `ellipsis`,
	 * not merely because it has a `width` — so a column left without one (to absorb whatever
	 * space the others don't need) gets browser `auto`-layout's unpredictable distribution
	 * of extra width instead of deterministically getting all of it. Pass `"fixed"` when a
	 * table relies on that pattern.
	 */
	tableLayout?: "fixed" | "auto"
}

export const TableConfiguration = <ColumnsDataType extends object>({
	columns,
	data,
	className,
	scrollY,
	expandable,
	isLoading,
	onRow,
	bordered = false,
	showHeader = true,
	virtual = false,
	scrollX,
	tableLayout,
}: Props<ColumnsDataType>) => {
	return (
		<TableObjectsProvider>
			<TableAnt
				className={className}
				columns={columns}
				dataSource={data}
				pagination={false}
				virtual={virtual}
				scroll={virtual ? { x: scrollX, y: scrollY } : { y: scrollY }}
				expandable={expandable}
				onRow={onRow}
				loading={isLoading}
				bordered={bordered}
				showHeader={showHeader}
				tableLayout={tableLayout}
			/>
		</TableObjectsProvider>
	)
}
